import {
  COMPOSIO_INTEGRATION_DEFINITIONS,
  type TIntegrationModalKey,
} from "@frontend-shared/constants";
import {
  EConnectorType,
  EIntegrationProvider,
  type IStaticIntegration,
  type TIntegrationIconComponent,
} from "@frontend-shared/types";
import { getAvailableConnectors } from "@frontend-shared/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useConnectIntegration } from "@/api/integration/useConnectIntegration";
import { useGetEnabledIntegrations } from "@/api/integration/useGetEnabledIntegrations";
import { Airtable } from "@/components/icons/integrations/Airtable";
import { Asana } from "@/components/icons/integrations/Asana";
import { Bamboohr } from "@/components/icons/integrations/Bamboohr";
import { Bitrix24 } from "@/components/icons/integrations/Bitrix24";
import { Calendly } from "@/components/icons/integrations/Calendly";
import { Clickup } from "@/components/icons/integrations/Clickup";
import { Confluence } from "@/components/icons/integrations/Confluence";
import { Crowdstrike } from "@/components/icons/integrations/Crowdstrike";
import { Databricks } from "@/components/icons/integrations/Databricks";
import { Docusign } from "@/components/icons/integrations/Docusign";
import { Dropbox } from "@/components/icons/integrations/Dropbox";
import { Fireflies } from "@/components/icons/integrations/Fireflies";
import { Github } from "@/components/icons/integrations/Github";
import { Gitlab } from "@/components/icons/integrations/Gitlab";
import { Gmail } from "@/components/icons/integrations/Gmail";
import { GoogleCalendar } from "@/components/icons/integrations/GoogleCalendar";
import { GoogleDrive } from "@/components/icons/integrations/GoogleDrive";
import { GoogleMeet } from "@/components/icons/integrations/GoogleMeet";
import { GoogleSheets } from "@/components/icons/integrations/GoogleSheets";
import { Hubspot } from "@/components/icons/integrations/Hubspot";
import { Intercom } from "@/components/icons/integrations/Intercom";
import { Jira } from "@/components/icons/integrations/Jira";
import { KonturDiadoc } from "@/components/icons/integrations/KonturDiadoc";
import { KonturFocus } from "@/components/icons/integrations/KonturFocus";
import { Linear } from "@/components/icons/integrations/Linear";
import { Mcp } from "@/components/icons/integrations/Mcp";
import { MicrosoftBookings } from "@/components/icons/integrations/MicrosoftBookings";
import { MicrosoftDataverse } from "@/components/icons/integrations/MicrosoftDataverse";
import { MicrosoftExcel } from "@/components/icons/integrations/MicrosoftExcel";
import { MicrosoftExchangeServer } from "@/components/icons/integrations/MicrosoftExchangeServer";
import { MicrosoftOneNote } from "@/components/icons/integrations/MicrosoftOneNote";
import { MicrosoftPlanner } from "@/components/icons/integrations/MicrosoftPlanner";
import { MicrosoftPowerBi } from "@/components/icons/integrations/MicrosoftPowerBi";
import { MicrosoftTeams } from "@/components/icons/integrations/MicrosoftTeams";
import { MicrosoftTodo } from "@/components/icons/integrations/MicrosoftTodo";
import { Nextcloud } from "@/components/icons/integrations/Nextcloud";
import { Notion } from "@/components/icons/integrations/Notion";
import { OneDrive } from "@/components/icons/integrations/OneDrive";
import { Outlook } from "@/components/icons/integrations/Outlook";
import { PowerAutomate } from "@/components/icons/integrations/PowerAutomate";
import { SharePoint } from "@/components/icons/integrations/SharePoint";
import { Slack } from "@/components/icons/integrations/Slack";
import { Smartsheet } from "@/components/icons/integrations/Smartsheet";
import { Telegram } from "@/components/icons/integrations/Telegram";
import { Todoist } from "@/components/icons/integrations/Todoist";
import { Trello } from "@/components/icons/integrations/Trello";
import { resolveIntegrationIcon } from "@/components/icons/integrations/vendored-logo";
import { Yandex } from "@/components/icons/integrations/Yandex";
import { Zendesk } from "@/components/icons/integrations/Zendesk";
import { ZohoAnalytics } from "@/components/icons/integrations/ZohoAnalytics";
import { ZohoCrm } from "@/components/icons/integrations/ZohoCrm";
import { ZohoDesk } from "@/components/icons/integrations/ZohoDesk";
import { ZohoMail } from "@/components/icons/integrations/ZohoMail";
import { ZohoWorkdrive } from "@/components/icons/integrations/ZohoWorkdrive";
import { Zoom } from "@/components/icons/integrations/Zoom";
import { useExternalIntegrationsProviderEnabled } from "@/hooks/use-external-integrations-provider-enabled";

import type { IStartConnectProps } from "./types";

export const INTEGRATION_ICON_MAP: Partial<
  Record<string, TIntegrationIconComponent>
> = {
  [EIntegrationProvider.AIRTABLE]: Airtable,
  [EIntegrationProvider.CALENDLY]: Calendly,
  [EIntegrationProvider.ATLASSIAN_CONFLUENCE]: Confluence,
  [EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC]: Confluence,
  [EIntegrationProvider.ATLASSIAN_JIRA]: Jira,
  [EIntegrationProvider.ATLASSIAN_JIRA_DC]: Jira,
  [EIntegrationProvider.AZURE_OUTLOOK]: Outlook,
  [EIntegrationProvider.AZURE_CALENDAR]: Outlook,
  [EIntegrationProvider.AZURE_ONEDRIVE]: OneDrive,
  [EIntegrationProvider.AZURE_SHARE_POINT]: SharePoint,
  [EIntegrationProvider.AZURE_EXCEL]: MicrosoftExcel,
  [EIntegrationProvider.AZURE_MICROSOFT_TEAMS]: MicrosoftTeams,
  [EIntegrationProvider.AZURE_MES]: MicrosoftExchangeServer,
  [EIntegrationProvider.GITHUB]: Github,
  [EIntegrationProvider.GITLAB]: Gitlab,
  [EIntegrationProvider.GOOGLE_DRIVE]: GoogleDrive,
  [EIntegrationProvider.GOOGLE_SHEETS]: GoogleSheets,
  [EIntegrationProvider.GOOGLE_CALENDAR]: GoogleCalendar,
  [EIntegrationProvider.GOOGLE_MEET]: GoogleMeet,
  [EIntegrationProvider.GOOGLE_MAIL]: Gmail,
  [EIntegrationProvider.HUBSPOT]: Hubspot,
  [EIntegrationProvider.NOTION]: Notion,
  [EIntegrationProvider.LINEAR]: Linear,
  [EIntegrationProvider.ZENDESK]: Zendesk,
  [EIntegrationProvider.SMARTSHEET]: Smartsheet,
  [EIntegrationProvider.DATABRICKS]: Databricks,
  [EIntegrationProvider.ZOHO_CRM]: ZohoCrm,
  [EIntegrationProvider.ZOHO_DESK]: ZohoDesk,
  [EIntegrationProvider.ZOHO_MAIL]: ZohoMail,
  [EIntegrationProvider.ZOHO_ANALYTICS]: ZohoAnalytics,
  [EIntegrationProvider.ZOHO_WORKDRIVE]: ZohoWorkdrive,
  [EIntegrationProvider.SLACK]: Slack,
  [EIntegrationProvider.TODOIST]: Todoist,
  [EIntegrationProvider.ASANA]: Asana,
  [EIntegrationProvider.BITRIX24]: Bitrix24,
  [EIntegrationProvider.CLICKUP]: Clickup,
  [EIntegrationProvider.FIREFLIES]: Fireflies,
  [EIntegrationProvider.CROWDSTRIKE]: Crowdstrike,
  [EIntegrationProvider.INTERCOM]: Intercom,
  [EIntegrationProvider.KONTUR_DIADOC]: KonturDiadoc,
  [EIntegrationProvider.KONTUR_FOCUS]: KonturFocus,
  [EIntegrationProvider.MCPS]: Mcp,
  [EIntegrationProvider.TELEGRAM]: Telegram,
  [EIntegrationProvider.BAMBOOHR]: Bamboohr,
  [EIntegrationProvider.DROPBOX]: Dropbox,
  [EIntegrationProvider.NEXTCLOUD]: Nextcloud,
  [EIntegrationProvider.TRELLO]: Trello,
  [EIntegrationProvider.ZOOM]: Zoom,
  [EIntegrationProvider.YANDEX]: Yandex,
  [EIntegrationProvider.DOCUSIGN]: Docusign,
  [EIntegrationProvider.MICROSOFT_BOOKINGS]: MicrosoftBookings,
  [EIntegrationProvider.ONENOTE]: MicrosoftOneNote,
  [EIntegrationProvider.MICROSOFT_PLANNER]: MicrosoftPlanner,
  [EIntegrationProvider.MICROSOFT_TODO]: MicrosoftTodo,
  [EIntegrationProvider.POWER_AUTOMATE]: PowerAutomate,
  [EIntegrationProvider.MICROSOFT_DATAVERSE]: MicrosoftDataverse,
  [EIntegrationProvider.MICROSOFT_POWER_BI]: MicrosoftPowerBi,
};

export const useIntegration = () => {
  const { t } = useTranslation("integration");

  const [isOpenMesIntegrationModel, setIsOpenMesIntegrationModel] =
    useState(false);
  const [isOpenFirefliesIntegrationModel, setIsOpenFirefliesIntegrationModel] =
    useState(false);
  const [
    isOpenCrowdstrikeIntegrationModel,
    setIsOpenCrowdstrikeIntegrationModel,
  ] = useState(false);
  const [
    isOpenKonturFocusIntegrationModel,
    setIsOpenKonturFocusIntegrationModel,
  ] = useState(false);
  const [
    isOpenKonturDiadocIntegrationModel,
    setIsOpenKonturDiadocIntegrationModel,
  ] = useState(false);
  const [isOpenMcpIntegrationModel, setIsOpenMcpIntegrationModel] =
    useState(false);
  const [isOpenTelegramIntegrationModel, setIsOpenTelegramIntegrationModel] =
    useState(false);
  const [isOpenBamboohrIntegrationModel, setIsOpenBamboohrIntegrationModel] =
    useState(false);
  const [isOpenNextcloudIntegrationModel, setIsOpenNextcloudIntegrationModel] =
    useState(false);

  // NOTE: Holds the integration definition whose connector the user must pick
  // when both NodeShift and Composio are available. Null when the modal is hidden.
  const [selectConnectorContext, setSelectConnectorContext] = useState<
    (typeof COMPOSIO_INTEGRATION_DEFINITIONS)[number] | null
  >(null);

  const [connectingProviders, setConnectingProviders] = useState<Set<string>>(
    new Set(),
  );

  const markConnecting = useCallback((provider: string): void => {
    setConnectingProviders((prev) => new Set(prev).add(provider));
  }, []);

  const unmarkConnecting = useCallback((provider: string): void => {
    setConnectingProviders((prev) => {
      const next = new Set(prev);
      next.delete(provider);
      return next;
    });
  }, []);

  // NOTE: When the user navigates back from the OAuth provider, the browser may restore
  // the page from bfcache with frozen React state — connectingProviders still has the provider.
  // Clearing on pageshow (persisted=true) re-enables the connect button.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        setConnectingProviders(new Set());
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const { mutate: connectIntegration } = useConnectIntegration();
  const {
    isEnabled: isExternalIntegrationsProviderEnabled,
    isLoading: isAppConfigLoading,
    isError: isAppConfigError,
  } = useExternalIntegrationsProviderEnabled();
  const {
    data: enabledIntegrations = [],
    enabledProviderInfo,
    isLoading: isEnabledIntegrationsLoading,
    isError: isEnabledIntegrationsError,
  } = useGetEnabledIntegrations();
  const isLoading = isAppConfigLoading || isEnabledIntegrationsLoading;
  const isError = isAppConfigError || isEnabledIntegrationsError;

  const modalOpeners: Record<TIntegrationModalKey, () => void> = useMemo(() => {
    return {
      mes: () => setIsOpenMesIntegrationModel(true),
      fireflies: () => setIsOpenFirefliesIntegrationModel(true),
      crowdstrike: () => setIsOpenCrowdstrikeIntegrationModel(true),
      konturFocus: () => setIsOpenKonturFocusIntegrationModel(true),
      konturDiadoc: () => setIsOpenKonturDiadocIntegrationModel(true),
      mcp: () => setIsOpenMcpIntegrationModel(true),
      telegram: () => setIsOpenTelegramIntegrationModel(true),
      bamboohr: () => setIsOpenBamboohrIntegrationModel(true),
      nextcloud: () => setIsOpenNextcloudIntegrationModel(true),
    };
  }, [
    setIsOpenMesIntegrationModel,
    setIsOpenFirefliesIntegrationModel,
    setIsOpenNextcloudIntegrationModel,
    setIsOpenCrowdstrikeIntegrationModel,
    setIsOpenKonturFocusIntegrationModel,
    setIsOpenKonturDiadocIntegrationModel,
    setIsOpenMcpIntegrationModel,
    setIsOpenTelegramIntegrationModel,
    setIsOpenBamboohrIntegrationModel,
  ]);

  // NOTE: Issue a connect mutation with an optional explicit connectorType.
  // Extracted from handleConnect so the SelectConnectorModal can call back with
  // the user's choice once they pick one.
  const startConnect = useCallback(
    ({ def, connectorType }: IStartConnectProps): void => {
      const provider = def.provider;
      if (!provider) return;

      markConnecting(provider);
      connectIntegration(
        {
          provider,
          ...(def.skipExternalProviderCheck
            ? {}
            : { isExternalIntegrationsProviderEnabled }),
          ...(def.integrationType
            ? { integrationType: def.integrationType }
            : {}),
          ...(connectorType ? { connectorType } : {}),
        },
        // NOTE: Only re-enable on error; on success the page navigates to the OAuth provider
        { onError: () => unmarkConnecting(provider) },
      );
    },
    [
      connectIntegration,
      isExternalIntegrationsProviderEnabled,
      markConnecting,
      unmarkConnecting,
    ],
  );

  const handleConnect = useCallback(
    (def: (typeof COMPOSIO_INTEGRATION_DEFINITIONS)[number]): void => {
      const provider = def.provider;
      if (!provider) return;

      // NOTE: Server-computed hint drives the picker modal — when both
      // connectors are available the user must pick one before connecting.
      const providerInfo = enabledProviderInfo.get(provider);
      if (providerInfo?.showConnectorPicker) {
        setSelectConnectorContext(def);
        return;
      }

      // NOTE: The config modal collects NATIVE credentials, so it must not open
      // when the native rail is unavailable — the user would enter a key nothing can
      // consume. showConnectorPicker is false here, so Pipedream is the only
      // remaining rail; pin it explicitly, because connectorType is also what tells
      // useConnectIntegration to follow the hosted URL rather than parse it
      // (NSAI-3765).
      if (
        providerInfo?.hasNodeshiftConnector === false &&
        providerInfo.hasPipedreamConnector
      ) {
        startConnect({ def, connectorType: EConnectorType.PIPEDREAM });
        return;
      }

      if (def.modalKey) {
        if (
          def.fallbackToExternalProvider &&
          isExternalIntegrationsProviderEnabled
        ) {
          markConnecting(provider);
          connectIntegration(
            { provider, isExternalIntegrationsProviderEnabled },
            // NOTE: Only re-enable on error; on success the page navigates to the OAuth provider
            { onError: () => unmarkConnecting(provider) },
          );
        } else {
          modalOpeners[def.modalKey]();
        }
        return;
      }

      startConnect({ def });
    },
    [
      connectIntegration,
      enabledProviderInfo,
      isExternalIntegrationsProviderEnabled,
      modalOpeners,
      markConnecting,
      unmarkConnecting,
      startConnect,
    ],
  );

  // NOTE: Callback invoked by the SelectConnectorModal once the user commits to
  // a connector. For INTERNAL on modal-based providers (Fireflies, MES, etc.)
  // we still need to open the provider-specific config modal so the user can
  // enter API keys / domain / token before the connect mutation fires.
  // COMPOSIO never needs the config modal — Composio handles credentials.
  const handleConnectorSelected = useCallback(
    (connectorType: EConnectorType): void => {
      if (!selectConnectorContext) return;
      const def = selectConnectorContext;

      if (def.modalKey && connectorType === EConnectorType.INTERNAL) {
        modalOpeners[def.modalKey]();
        return;
      }

      startConnect({ def, connectorType });
    },
    [selectConnectorContext, modalOpeners, startConnect],
  );

  // NOTE: Connectors available for the picker — derived from the server-side
  // enabledProviderInfo so the picker reflects the same source of truth as the
  // routing decision in handleConnect.
  const selectConnectorAvailable = useMemo<EConnectorType[]>(() => {
    if (!selectConnectorContext?.provider) return [];
    return getAvailableConnectors(
      enabledProviderInfo.get(selectConnectorContext.provider),
    );
  }, [selectConnectorContext, enabledProviderInfo]);

  // NOTE: Base array without isConnecting; rebuilt only when integration definitions or deps change
  const staticIntegrationBase = useMemo(() => {
    return COMPOSIO_INTEGRATION_DEFINITIONS.map((def) => ({
      id: def.id,
      name: t(def.nameKey as never),
      icon: resolveIntegrationIcon({
        providerId: def.id,
        authored: INTEGRATION_ICON_MAP[def.id],
      }),
      provider: def.provider,
      description: t(def.descriptionKey as never),
      isConnecting: false,
      onConnect: () => handleConnect(def),
    }));
  }, [t, handleConnect]);

  // NOTE: Cheap re-map that only updates isConnecting without rebuilding the full integration array
  const staticIntegration: IStaticIntegration[] = useMemo(() => {
    return (
      staticIntegrationBase
        .map((item) => ({
          ...item,
          isConnecting: connectingProviders.has(item.provider ?? ""),
        }))
        .filter((integration) => enabledIntegrations.includes(integration.id))
        // NOTE: Org-scoped connectors carry isOrgConfigured from the server
        // and are hidden when it is false: Atlassian Data Center when the
        // current project's org has not configured that DC product, and
        // Nextcloud when the project is personal (its config is org-level).
        // Every other provider omits the flag and stays.
        .filter(
          (integration) =>
            enabledProviderInfo.get(integration.id)?.isOrgConfigured !== false,
        )
    );
  }, [
    staticIntegrationBase,
    connectingProviders,
    enabledIntegrations,
    enabledProviderInfo,
  ]);

  return {
    isLoading,
    isError,
    staticIntegration,
    isOpenMesIntegrationModel,
    closeMesIntegrationModel: () => setIsOpenMesIntegrationModel(false),
    isOpenFirefliesIntegrationModel,
    closeFirefliesIntegrationModel: () =>
      setIsOpenFirefliesIntegrationModel(false),
    isOpenCrowdstrikeIntegrationModel,
    closeCrowdstrikeIntegrationModel: () =>
      setIsOpenCrowdstrikeIntegrationModel(false),
    isOpenKonturDiadocIntegrationModel,
    closeKonturDiadocIntegrationModel: () =>
      setIsOpenKonturDiadocIntegrationModel(false),
    isOpenKonturFocusIntegrationModel,
    closeKonturFocusIntegrationModel: () =>
      setIsOpenKonturFocusIntegrationModel(false),
    isOpenMcpIntegrationModel,
    closeMcpIntegrationModel: () => setIsOpenMcpIntegrationModel(false),
    isOpenTelegramIntegrationModel,
    closeTelegramIntegrationModel: () =>
      setIsOpenTelegramIntegrationModel(false),
    isOpenBamboohrIntegrationModel,
    closeBamboohrIntegrationModel: () =>
      setIsOpenBamboohrIntegrationModel(false),
    isOpenNextcloudIntegrationModel,
    closeNextcloudIntegrationModel: () =>
      setIsOpenNextcloudIntegrationModel(false),
    selectConnectorContext,
    selectConnectorAvailable,
    closeSelectConnectorModal: () => setSelectConnectorContext(null),
    handleConnectorSelected,
  };
};
